import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { BILL_CATALOG } from '@/consts/billing'
import { REAL_RECHARGE_BILL_SUBTYPES } from '@/models/bill'
import { FOLLOW_TASK_PLATFORM } from '@/models/social-media'
import { useUserStore } from '@/stores/user'

export interface OnboardingTasksState {
  followXDone: boolean
  firstDepositDone: boolean
}

export interface OnboardingTasksResult {
  tasks: OnboardingTasksState
  /** True until the first refresh after `enabled` completes; resets when disabled. */
  loading: boolean
}

async function fetchFirstDepositDone(): Promise<boolean> {
  try {
    const res = await API.getUserBills({
      page: 1,
      per_page: 100,
      catalog: BILL_CATALOG.OTHERS,
    })
    if (res.code !== 0) return false
    return (res.bills ?? []).some((bill: any) => REAL_RECHARGE_BILL_SUBTYPES.includes(bill.subType))
  } catch {
    return false
  }
}

async function fetchFollowXDone(): Promise<boolean> {
  const platform = FOLLOW_TASK_PLATFORM.x
  if (platform == null) return false

  try {
    const res = await API.getFollowTask({ platform })
    return res.code === 0 && res.followed
  } catch {
    return false
  }
}

/**
 * Task completion states for the onboarding guide (header badge + modal).
 * X follow is complete once GET /user/follow_task confirms the account follows X.
 * While enabled, refreshes on mount and whenever the user returns to the tab
 * (covers coming back after linking X on the profile page or after a deposit).
 * Call once in Header and pass the result down, so badge and modal share one fetch.
 */
export function useOnboardingTasks(enabled: boolean): OnboardingTasksResult {
  const [followXDone, setFollowXDone] = useState(false)
  const [firstDepositDone, setFirstDepositDone] = useState(false)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const [, nextFollowXDone, depositDone] = await Promise.all([
      useUserStore
        .getState()
        .getUserInfo()
        .catch(() => {}),
      fetchFollowXDone(),
      fetchFirstDepositDone(),
    ])
    setFollowXDone(nextFollowXDone)
    setFirstDepositDone(depositDone)
  }, [])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    void refresh().finally(() => {
      if (!cancelled) setLoading(false)
    })

    // Background refreshes on return-to-tab update silently, without re-showing loading.
    const onReturnToTab = () => {
      if (document.visibilityState !== 'visible') return
      void refresh()
    }

    document.addEventListener('visibilitychange', onReturnToTab)
    window.addEventListener('focus', onReturnToTab)
    return () => {
      cancelled = true
      setLoading(true)
      document.removeEventListener('visibilitychange', onReturnToTab)
      window.removeEventListener('focus', onReturnToTab)
    }
  }, [enabled, refresh])

  return { tasks: { followXDone, firstDepositDone }, loading }
}
